import React, { useMemo, useState, useEffect } from 'react';
import SignalCard from './SignalCard';
import AssetDetail from './AssetDetail';
import { useFreemium } from '../hooks/useFreemium';
import { PaywallBanner } from './PaywallOverlay';
import PremiumBanner from './PremiumBanner';
import './DiscoverView.css';

const PREFERRED_SECTORS_KEY = 'bullivo_preferred_sectors_v1';

function loadPreferredSectors() {
  try {
    const v = localStorage.getItem(PREFERRED_SECTORS_KEY);
    return v ? JSON.parse(v) : [];
  } catch { return []; }
}

function sortByPreference(assets, preferredSet) {
  if (!preferredSet || preferredSet.size === 0) return assets;
  return [...assets].sort((a, b) => {
    const aPref = preferredSet.has(a.sector);
    const bPref = preferredSet.has(b.sector);
    if (aPref !== bPref) return aPref ? -1 : 1;
    return (b.qualityScore ?? 0) - (a.qualityScore ?? 0);
  });
}

function calcMarketStatus(assets) {
  if (!assets.length) return null;
  const avg7d     = assets.reduce((sum, a) => sum + (a.change7d ?? 0), 0) / assets.length;
  const buyCount  = assets.filter(a => a.signal === 'Kaufen').length;
  const sellCount = assets.filter(a => a.signal === 'Verkaufen').length;
  return { isBull: avg7d >= 0 || buyCount >= sellCount, avg7d, buyCount, total: assets.length };
}

function DiscoverView({ assets, loading, error, lastUpdated, refresh, onAddToPortfolio, onRegisterDetailCloser, onShowPaywall }) {
  const [selectedAsset, setSelectedAsset] = useState(null);
  const { getLimit, isPremium, freemiumActive } = useFreemium();
  const reboundActive = (() => {
    try { return localStorage.getItem('bullivo_rebound_mode') === '1'; } catch { return false; }
  })();

  useEffect(() => {
    onRegisterDetailCloser?.(selectedAsset ? () => setSelectedAsset(null) : null);
  }, [selectedAsset, onRegisterDetailCloser]);

  const discoverLimit = getLimit('discoverSignals');

  const [preferredSectors, setPreferredSectors] = useState(loadPreferredSectors);

  // Listen auf localStorage-Aenderungen aus SettingsView
  useEffect(() => {
    const onStorage = () => setPreferredSectors(loadPreferredSectors());
    window.addEventListener('storage', onStorage);
    // Custom event fuer Same-Tab-Updates (SettingsView feuert das)
    window.addEventListener('bullivo:preferredSectorsChanged', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('bullivo:preferredSectorsChanged', onStorage);
    };
  }, []);

  const buyAssets = useMemo(() => {
    const filtered = assets.filter(a => a.signal === 'Kaufen');
    return sortByPreference(filtered, new Set(preferredSectors));
  }, [assets, preferredSectors]);

  const visibleAssets = discoverLimit != null ? buyAssets.slice(0, discoverLimit) : buyAssets;
  const lockedCount = discoverLimit != null ? Math.max(buyAssets.length - discoverLimit, 0) : 0;

  const market = useMemo(() => calcMarketStatus(assets), [assets]);

  if (error) {
    return (
      <div className="discover-error">
        <p>⚠️ Screener nicht erreichbar</p>
        <button onClick={refresh}>Neu laden</button>
      </div>
    );
  }

  if (loading && !lastUpdated) {
    return (
      <div className="discover-view">
        <div className="market-status-skeleton shimmer" />
        {[1,2,3,4,5,6].map(i => <div key={i} className="skeleton-card shimmer" />)}
      </div>
    );
  }

  return (
    <div className="discover-view">

      {reboundActive && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', margin: '0 0 12px', background: 'linear-gradient(90deg, #ff6b35 0%, #ff8c42 100%)', color: '#fff', borderRadius: 12, fontWeight: 600, fontSize: 14 }}>
          <span style={{ fontSize: 16 }}>⚡</span>
          <span>Rebound-Modus aktiv — aggressive Picks an der unteren Schwelle</span>
        </div>
      )}

      {market && (
        <div className={`market-hero ${market.isBull ? 'bull' : 'bear'}`}>
          <div className="market-hero-blur" />
          <div className="market-hero-content">
            <div className="market-hero-text">
              <span className="market-hero-eyebrow">Market Sentiment</span>
              <h1 className="market-hero-title">{market.isBull ? 'Bull Market' : 'Bear Market'}</h1>
              <p className="market-hero-desc">
                {market.buyCount} von {market.total} Signalen auf Buy.
                {market.isBull
                  ? ' Quantitative Analyse zeigt Wachstumsmuster in Tech- und Energiesektoren.'
                  : ' Erhöhte Volatilität – defensivere Positionierung empfohlen.'}
              </p>
            </div>
            <div className="market-hero-stat">
              <span className="market-hero-stat-label">Ø Signal-Stärke</span>
              <span className="market-hero-stat-value">
                {market.avg7d >= 0 ? '+' : ''}{market.avg7d.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      )}

      {buyAssets.length === 0 ? (
        <p className="discover-empty">
          {reboundActive
            ? 'Aktuell keine Rebound-Picks — Markt ist nicht oversold. Prüfe später wieder oder wechsle in den Standard-Modus.'
            : 'Aktuell keine Kaufempfehlungen.'}
        </p>
      ) : (
        <>
          {visibleAssets.map(asset => (
            <SignalCard key={asset.ticker} asset={asset} onSelect={setSelectedAsset} rebound={reboundActive} />
          ))}
          {lockedCount > 0 && (
            <PaywallBanner count={lockedCount} onUpgrade={() => onShowPaywall?.()} />
          )}
          {freemiumActive && !isPremium && lockedCount === 0 && (
            <PremiumBanner onUpgrade={() => onShowPaywall?.()} />
          )}
        </>
      )}

      {selectedAsset && (
        <AssetDetail
          asset={selectedAsset}
          onClose={() => setSelectedAsset(null)}
          onAddToPortfolio={onAddToPortfolio}
        />
      )}

      <p className="discover-disclaimer">
        {lastUpdated && `${buyAssets.length} Kaufempfehlungen · Stand ${lastUpdated.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} · `}
        Kein Real-Money Trading · Nur Informationszweck
      </p>
    </div>
  );
}

export default DiscoverView;
